import React, { useEffect, useState } from 'react';
import { Modal } from './Modal';
import { Lock, ArrowRight, Shield, RefreshCw, AlertCircle } from 'lucide-react';
interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
}
export function LoginModal({
  isOpen,
  onClose
}: LoginModalProps) {
  const [view, setView] = useState<'login' | 'forgot'>('login');
  const [status, setStatus] = useState<'idle' | 'loading' | 'error' | 'success'>('idle');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setTimeout(() => {
        setView('login');
        setStatus('idle');
        setEmail('');
        setPassword('');
      }, 300);
    }
  }, [isOpen]);
  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) return;
    setStatus('loading');
    // Simulate API call that fails (since this is a demo)
    setTimeout(() => {
      setStatus('error');
    }, 1500);
  };
  const handleForgot = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setStatus('loading');
    // Simulate API call that succeeds
    setTimeout(() => {
      setStatus('success');
    }, 1500);
  };
  return <Modal isOpen={isOpen} onClose={onClose} className="max-w-md" data-id="element-150">
      <div className="p-8 md:p-10" data-id="element-151">
        <div className="flex items-center gap-2 mb-2 text-gold-400/70 text-[10px] font-mono tracking-[0.3em] uppercase" data-id="element-152">
          <Shield className="w-3 h-3" data-id="element-153" />
          Secure Channel
        </div>

        <div className="flex items-center gap-3 mb-8" data-id="element-154">
          <div className="p-2 bg-base-950 border border-slate-800" data-id="element-155">
            <Lock className="w-5 h-5 text-gold-400" data-id="element-156" />
          </div>
          <h2 className="text-2xl font-bold text-white tracking-tight" data-id="element-157">
            {view === 'login' ? 'Investor Login' : 'Reset Password'}
          </h2>
        </div>

        {view === 'login' ? <form className="space-y-5" onSubmit={handleLogin} data-id="element-158">
            <div data-id="element-159">
              <label className="block text-xs font-bold tracking-widest text-slate-400 uppercase mb-2" data-id="element-160">
                Investor ID / Email
              </label>
              <input type="text" value={email} onChange={e => setEmail(e.target.value)} className="w-full bg-base-950 border border-slate-800 px-4 py-3 text-white focus:outline-none focus:border-gold-400 transition-colors font-mono text-sm" placeholder="Enter your ID" required data-id="element-161" />
            </div>

            <div data-id="element-162">
              <div className="flex justify-between items-center mb-2" data-id="element-163">
                <label className="block text-xs font-bold tracking-widest text-slate-400 uppercase" data-id="element-164">
                  Password
                </label>
                <button type="button" onClick={() => {
              setView('forgot');
              setStatus('idle');
            }} className="text-xs text-gold-400 hover:text-gold-300" data-id="element-165">
                  Forgot?
                </button>
              </div>
              <input type="password" value={password} onChange={e => setPassword(e.target.value)} className="w-full bg-base-950 border border-slate-800 px-4 py-3 text-white focus:outline-none focus:border-gold-400 transition-colors font-mono text-sm tracking-widest" placeholder="••••••••" required data-id="element-166" />
            </div>

            {status === 'error' && <div className="flex items-center gap-2 text-red-400 text-xs font-mono bg-red-400/10 p-3 border border-red-400/20" data-id="element-167">
                <AlertCircle className="w-4 h-4" data-id="element-168" />
                Invalid credentials or account locked.
              </div>}

            <button type="submit" disabled={status === 'loading'} className="w-full flex items-center justify-center gap-2 bg-gold-400 text-slate-950 font-bold tracking-widest uppercase py-4 mt-6 hover:bg-gold-300 transition-colors clip-corner disabled:opacity-70 disabled:cursor-not-allowed" data-id="element-169">
              {status === 'loading' ? <RefreshCw className="w-4 h-4 animate-spin" data-id="element-170" /> : <>
                  Sign In
                  <ArrowRight className="w-4 h-4" data-id="element-171" />
                </>}
            </button>
          </form> : <form className="space-y-5" onSubmit={handleForgot} data-id="element-172">
            {status === 'success' ? <div className="py-6 text-center" data-id="element-173">
                <p className="text-slate-300 text-sm mb-6" data-id="element-174">
                  If an account exists for{' '}
                  <strong className="text-white" data-id="element-175">{email}</strong>, you will
                  receive a password reset link shortly.
                </p>
                <button type="button" onClick={() => {
            setView('login');
            setStatus('idle');
          }} className="px-6 py-3 border border-slate-700 text-slate-300 text-sm tracking-wide hover:border-gold-400/50 hover:text-gold-300 transition-all duration-300" data-id="element-176">
                  Return to Login
                </button>
              </div> : <>
                <p className="text-slate-400 text-sm mb-6" data-id="element-177">
                  Enter your Investor ID or email address. We will send you
                  instructions to reset your password.
                </p>
                <div data-id="element-178">
                  <label className="block text-xs font-bold tracking-widest text-slate-400 uppercase mb-2" data-id="element-179">
                    Investor ID / Email
                  </label>
                  <input type="text" value={email} onChange={e => setEmail(e.target.value)} className="w-full bg-base-950 border border-slate-800 px-4 py-3 text-white focus:outline-none focus:border-gold-400 transition-colors font-mono text-sm" placeholder="Enter your ID" required data-id="element-180" />
                </div>

                <button type="submit" disabled={status === 'loading'} className="w-full flex items-center justify-center gap-2 bg-gold-400 text-slate-950 font-bold tracking-widest uppercase py-4 mt-6 hover:bg-gold-300 transition-colors clip-corner disabled:opacity-70 disabled:cursor-not-allowed" data-id="element-181">
                  {status === 'loading' ? <RefreshCw className="w-4 h-4 animate-spin" data-id="element-182" /> : <>
                      Send Reset Link
                      <ArrowRight className="w-4 h-4" data-id="element-183" />
                    </>}
                </button>
                <div className="text-center mt-4" data-id="element-184">
                  <button type="button" onClick={() => {
              setView('login');
              setStatus('idle');
            }} className="text-xs text-slate-500 hover:text-white transition-colors" data-id="element-185">
                    Back to Login
                  </button>
                </div>
              </>}
          </form>}

        <div className="mt-8 pt-6 border-t border-slate-800 text-center" data-id="element-186">
          <p className="text-xs text-slate-500 leading-relaxed font-mono" data-id="element-187">
            This system is restricted to authorized users only. All activities
            are monitored and logged.
          </p>
        </div>
      </div>
    </Modal>;
}